import React, { useMemo } from 'react';
import { FRONT_MUSCLES } from './data/musclesFront';
import { BACK_MUSCLES } from './data/musclesBack';
import { PART_TO_MUSCLE, MUSCLE_BY_ID } from './muscleDefinitions';

/*
 * MuscleMap — modelo anatómico SVG interativo, reutilizável.
 *
 * Props:
 *   view               "front" | "back"                    (controlado pelo pai — botões Frente/Costas)
 *   selectedMuscleIds  array de ids canónicos selecionados (ex.: ["pectoralis-major"]) — a vermelho
 *   secondaryMuscleIds array de ids canónicos secundários (opcional) — a laranja; clicar neles
 *                      também chama onMuscleToggle (é o pai que decide o que um clique significa)
 *   onMuscleToggle     (muscle) => void   com { muscleId, name, view } — alterna (adiciona/remove)
 *   height             altura em px (default 460)
 *
 * Multi-seleção com toggle: clicar num músculo adiciona-o; clicar num já vermelho remove-o.
 * Cada <path> é uma sub-região do body-muscles com data-muscle = id canónico.
 * Sub-regiões que não pertencem a um músculo (cabeça, mãos, pés…) são "contexto":
 * desenhadas mas não clicáveis. A cor vermelha é usada nos músculos selecionados.
 */

const VIEWBOX = { front: '0 0 35 93', back: '37 0 35 93' };

export default function MuscleMap({ view = 'front', selectedMuscleIds = [], secondaryMuscleIds = [], intensity = null, intensityTones = 4, describeMuscle, onMuscleToggle, height = 460, legend = null }) {
  const parts = view === 'front' ? FRONT_MUSCLES : BACK_MUSCLES;
  const viewBox = VIEWBOX[view] || VIEWBOX.front;
  const selectedSet = new Set(Array.isArray(selectedMuscleIds) ? selectedMuscleIds : []);
  const secondarySet = new Set(Array.isArray(secondaryMuscleIds) ? secondaryMuscleIds : []);
  // Modo CALOR: cada músculo recebe um tom (1..intensityTones) definido por quem chama. O componente não decide o
  // significado dos tons — quem pinta sabe se é recuperação, volume ou fadiga, e é aí que a legenda tem de estar.
  const tone = (muscleId) => {
    if (!intensity) return 0;
    const raw = typeof intensity.get === 'function' ? intensity.get(muscleId) : intensity[muscleId];
    const level = Math.round(Number(raw) || 0);
    return Math.min(Math.max(level, 0), intensityTones);
  };

  const toggle = (muscleId) => {
    if (!muscleId || typeof onMuscleToggle !== 'function') return;
    const def = MUSCLE_BY_ID[muscleId];
    onMuscleToggle({ muscleId, name: def ? def.name : muscleId, view });
  };

  return (
    <div className="muscle-map-wrap" style={{ width: '100%' }}>
      <style>{`
        .muscle-map-wrap svg { width: 100%; height: auto; display: block; background: #ffffff; border-radius: 12px; }
        .muscle-map-wrap .mm-context { fill: #eef2f7; stroke: #334155; stroke-width: 0.12; pointer-events: none; }
        .muscle-map-wrap .mm-muscle { fill: #f3f5f8; stroke: #334155; stroke-width: 0.15; cursor: pointer; transition: fill .12s ease; }
        .muscle-map-wrap .mm-muscle:hover { fill: #ffd6d6; }
        .muscle-map-wrap .mm-muscle:focus { outline: none; fill: #ffd6d6; }
        .muscle-map-wrap .mm-muscle.secondary { fill: #fdba74; }
        .muscle-map-wrap .mm-muscle.selected { fill: #ef4444; }
        /* Calor: tons do mesmo matiz, do mais claro (há mais tempo) ao mais forte (há menos tempo). */
        .muscle-map-wrap .mm-muscle.heat-1 { fill: #fef3c7; }
        .muscle-map-wrap .mm-muscle.heat-2 { fill: #fcd34d; }
        .muscle-map-wrap .mm-muscle.heat-3 { fill: #f59e0b; }
        .muscle-map-wrap .mm-muscle.heat-4 { fill: #b45309; }
        .muscle-map-wrap .mm-legend { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-top: 8px; font-size: 12px; color: #334155; }
        .muscle-map-wrap .mm-legend-swatch { display: inline-block; width: 14px; height: 14px; border-radius: 4px; border: 1px solid #334155; margin-right: 4px; vertical-align: -3px; }
      `}</style>
      <svg
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{ maxHeight: height }}
        role="group"
        aria-label={view === 'front' ? 'Modelo anatómico — vista frontal' : 'Modelo anatómico — vista posterior'}
      >
        {parts.map((p) => {
          const muscleId = PART_TO_MUSCLE[p.id] || null;
          if (!muscleId) {
            // Contexto (não-músculo): desenhado, não interativo.
            return <path key={p.id} d={p.path} className="mm-context" />;
          }
          const def = MUSCLE_BY_ID[muscleId];
          const isSel = selectedSet.has(muscleId);
          const isSecondary = !isSel && secondarySet.has(muscleId);
          const heat = !isSel && !isSecondary ? tone(muscleId) : 0;
          const cls = isSel ? 'mm-muscle selected' : isSecondary ? 'mm-muscle secondary' : heat > 0 ? `mm-muscle heat-${heat}` : 'mm-muscle';
          const baseName = def ? def.name : muscleId;
          const described = typeof describeMuscle === 'function' ? describeMuscle(muscleId) : null;
          return (
            <path
              key={p.id}
              id={p.id}
              data-muscle={muscleId}
              data-level={heat || undefined}
              className={cls}
              d={p.path}
              role="button"
              tabIndex={0}
              aria-label={described ? `${baseName} — ${described}` : baseName}
              title={described ? `${baseName} — ${described}` : baseName}
              aria-pressed={isSel || isSecondary}
              onClick={() => toggle(muscleId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(muscleId); }
              }}
            />
          );
        })}
      </svg>
      {legend ? <div className="mm-legend" data-testid="muscle-map-legend">{legend}</div> : null}
    </div>
  );
}

/**
 * Gera uma STRING SVG autónoma (para preview / compatibilidade com o campo imageUrl)
 * de uma vista, com os músculos indicados destacados a vermelho. Não faz pedidos externos.
 * Aceita um id ou um array de ids.
 */
export function buildMuscleSvgString(muscleIds, view = 'front') {
  const ids = Array.isArray(muscleIds) ? muscleIds : (muscleIds ? [muscleIds] : []);
  const parts = view === 'back' ? BACK_MUSCLES : FRONT_MUSCLES;
  const viewBox = VIEWBOX[view] || VIEWBOX.front;

  const selParts = new Set();
  ids.forEach((id) => {
    const def = MUSCLE_BY_ID[id];
    if (def && def.view === view) def.sourceParts.forEach((p) => selParts.add(p));
  });

  const paths = parts.map((p) => {
    const isMuscle = !!PART_TO_MUSCLE[p.id];
    const fill = selParts.has(p.id) ? '#ef4444' : (isMuscle ? '#f3f5f8' : '#eef2f7');
    return `<path d="${p.path}" fill="${fill}" stroke="#334155" stroke-width="0.13"/>`;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet"><rect x="${view === 'back' ? 37 : 0}" y="0" width="35" height="93" fill="#ffffff"/>${paths}</svg>`;
}

/** Converte o SVG dos músculos num data URL (para guardar em imageUrl como preview). */
export function buildMuscleImageDataUrl(muscleIds, view = 'front') {
  const svg = buildMuscleSvgString(muscleIds, view);
  if (!svg) return '';
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
